import { Head } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import ScheduleForm from "./_form"

interface Customer {
  id: number
  display_name: string
}

interface UserOption {
  id: number
  name: string
}

interface ContactOption {
  id: number
  name: string
}

interface ScheduleData {
  id: number
  title: string
  notes: string
  start_date: string | null
  end_date: string | null
  no_start: boolean
  no_end: boolean
  leader_id: number | null
  customer_user_id: number | null
  investigator_ids: number[]
}

interface Props {
  customer: Customer
  schedule: ScheduleData
  leaders: UserOption[]
  investigators: UserOption[]
  contacts: ContactOption[]
  errors?: Record<string, string[]>
}

export default function Edit({
  customer,
  schedule,
  leaders,
  investigators,
  contacts,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Schedule", href: `/customers/${customer.id}/schedules` },
    {
      title: "Edit",
      href: `/customers/${customer.id}/schedules/${schedule.id}/edit`,
    },
  ]

  const { data, setData, patch, processing, transform } = useForm<ScheduleData>(
    {
      id: schedule.id,
      title: schedule.title,
      notes: schedule.notes,
      start_date: schedule.start_date,
      end_date: schedule.end_date,
      no_start: schedule.no_start,
      no_end: schedule.no_end,
      leader_id: schedule.leader_id,
      customer_user_id: schedule.customer_user_id,
      investigator_ids: schedule.investigator_ids,
    },
  )

  transform((data) => ({ customer_schedule: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/customers/${customer.id}/schedules/${schedule.id}`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Meeting - ${customer.display_name}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">
          Update Meeting for {customer.display_name}
        </h1>
        <ScheduleForm
          customerId={customer.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Save"
          leaders={leaders}
          investigators={investigators}
          contacts={contacts}
        />
      </div>
    </AppLayout>
  )
}
